const { ethers, utils } = require('ethers');
const { ecsign } = require('ethereumjs-util');
const { keccak256, defaultAbiCoder, solidityPack } = utils;
const { 
  _getChainSpecificConstants, 
  _extractChainID, 
  customErrors, 
  signatureEnum, 
  _RAGE_QUIT_PACKET_TYPEHASH } = require('./constants');
const { getDomainSeparator } = require('./utils-712');
const { _add0x, _getKNOTNonce } = require('./utils');
const { getContractInstance } = require('./contracts');

const getKnotInfoHash = (stakehouseAddress, blsPublicKey, recipient) => {
    return keccak256(
      solidityPack(
        ['address', 'bytes', 'address'],
        [stakehouseAddress, blsPublicKey, recipient]
      )
    );
};

const getRageQuitDigest = (
    chainID, 
    contractAddress, 
    contractName, 
    knotInfoHash, 
    deadline, 
    blsPubKeyInternalNonce) => {

    const DOMAIN_SEPARATOR = getDomainSeparator(contractAddress, contractName, chainID);
  
    return keccak256(
      solidityPack(
        ['bytes1', 'bytes1', 'bytes32', 'bytes32'],
        [
          '0x19',
          '0x01',
          DOMAIN_SEPARATOR,
          keccak256(
            defaultAbiCoder.encode(
              ['bytes32', 'bytes32', 'uint256', 'uint256'], // type hash, packet hash, deadline, nonce
              [_RAGE_QUIT_PACKET_TYPEHASH, knotInfoHash, deadline.toString(), blsPubKeyInternalNonce.toString()]
            )
          )
        ]
      )
    );
};

const _signRageQuitRequestWithPrivateKey = async (
  signer, 
  stakehouseAddress, 
  blsPublicKey, 
  recipient, 
  deadline, 
  signatureHolder,
  privateKey,
  stakehouseKick=null) => {
  
  const chainID = await _extractChainID(signer);
  const { stakehouseAddresses } = _getChainSpecificConstants(chainID);

  if(!(signatureHolder in signatureEnum)) {
      throw customErrors.INVALID_SIGNATURE_HOLDER;
  }

  let contractInfo;
  let contractAddress;

  if(stakehouseKick) {
    contractInfo = 'HouseRageQuitAdaptor';
    contractAddress = stakehouseAddresses.HOUSE_RAGE_QUIT_ADAPTOR;
  }
  else {
    contractInfo = 'TransactionRouter';
    contractAddress = stakehouseAddresses.TRANSACTION_ROUTER;
  }

  let blsInternalNonce = await _getKNOTNonce(signer, blsPublicKey);

  blsInternalNonce = blsInternalNonce.add(ethers.BigNumber.from(signatureEnum[signatureHolder]));

  const knotInfoHash = getKnotInfoHash(stakehouseAddress, _add0x(blsPublicKey), recipient);

  const digest = getRageQuitDigest(
      chainID,
      contractAddress,
      contractInfo,
      knotInfoHash,
      deadline,
      blsInternalNonce
  );

  let data;

  data = ecsign(
    Buffer.from(digest.slice(2), 'hex'),
    Buffer.from(privateKey.slice(2), 'hex')
  );
  return data;
};

const _signRageQuitRequest = async (
    provider,
    signerAddress,
    stakehouseAddress, 
    blsPublicKey, 
    recipient, 
    deadline, 
    signatureHolder,
    stakehouseKick=null) => {
    
    const chainID = await _extractChainID(provider);
    const { stakehouseAddresses } = _getChainSpecificConstants(chainID);

    if(!(signatureHolder in signatureEnum)) {
        throw customErrors.INVALID_SIGNATURE_HOLDER;
    }

    let contractInfo;
    let contractAddress;

    if(stakehouseKick) {
      contractInfo = 'HouseRageQuitAdaptor';
      contractAddress = stakehouseAddresses.HOUSE_RAGE_QUIT_ADAPTOR;
    }
    else {
      contractInfo = 'TransactionRouter';
      contractAddress = stakehouseAddresses.TRANSACTION_ROUTER;
    }

    let blsInternalNonce = await _getKNOTNonce(provider, blsPublicKey);

    blsInternalNonce = blsInternalNonce.add(ethers.BigNumber.from(signatureEnum[signatureHolder]));

    const knotInfoHash = getKnotInfoHash(stakehouseAddress, _add0x(blsPublicKey), recipient);

    const digest = getRageQuitDigest(
        chainID,
        contractAddress,
        contractInfo,
        knotInfoHash,
        deadline,
        blsInternalNonce
    );

    if (!provider) {
      throw new Error('provider not found');
    }
    const sig = await new Promise((resolve, reject) => {
      // @ts-expect-error
      provider.send(
      {
        method: 'eth_sign',
        params: [signerAddress, digest.toString()],
      },
      (error, response) => {
        if (error) {
          return reject(error);
        }
        return resolve(response.result);
      },
      );
    });
    
    const {v, r, s} = utils.splitSignature(sig);
    
    return {v, r, s};
};

const _multipartyRageQuit = async (
    signer, 
    stakehouseAddress, 
    ethRecipient, 
    freeFloatingSlotOwner=null, 
    authenticatedReport, 
    reportAndTokenHolderSignatures, 
    ethValue,
    stakehouseKick=null) => {

    if(reportAndTokenHolderSignatures.length < 3) {
        throw customErrors.INVALID_SIGNATURE_ARRAY_LENGTH;
    }

    authenticatedReport.report.blsPublicKey = _add0x(authenticatedReport.report.blsPublicKey);
    authenticatedReport.report.withdrawalCredentials = _add0x(authenticatedReport.report.withdrawalCredentials);

    const chainID = await _extractChainID(signer);
    const contractInstance = await getContractInstance(signer);

    if(stakehouseKick) {
      const contract = contractInstance.houseRagequitAdaptor();

      return contract.multipartyRageQuit(
        stakehouseAddress,
        authenticatedReport.report.blsPublicKey,
        ethRecipient,
        freeFloatingSlotOwner,
        authenticatedReport.report,
        reportAndTokenHolderSignatures,
        { value: ethValue }
      );
    }
    // goerli chain multiparty ragequit
    else if(chainID === 5) {
      /*
      ** the already deployed contract on goerli doesn't use the 
      ** freeFloatingSlotOwner parameter, so for now, it gets it's seperate
      ** ABI for multiparty.
      ** The related changes in the SDK shall be later reverted
      ** when the Stakehouse smart contracts are redeployed.
      */
      const contract = contractInstance.goerliMultipartyRagequit();

      return contract.multipartyRageQuit(
        stakehouseAddress,
        authenticatedReport.report.blsPublicKey,
        ethRecipient,
        authenticatedReport.report,
        reportAndTokenHolderSignatures,
        { value: ethValue }
      );
    }
    else {
      const contract = contractInstance.transactionRouter();

      return contract.multipartyRageQuit(
        stakehouseAddress,
        authenticatedReport.report.blsPublicKey,
        ethRecipient,
        authenticatedReport.report,
        reportAndTokenHolderSignatures,
        { value: ethValue }
      );
    }
};

module.exports = {
    _signRageQuitRequestWithPrivateKey,
    _signRageQuitRequest,
    _multipartyRageQuit
};
